import React, { useState, useEffect } from 'react';
import { getCurrentUser, signOut } from 'aws-amplify/auth';
import { refreshSession } from '../../services/RefreshSession';
import { setupConsentListener } from '../../utils/gaConsent';
import AcademicApplicationService from '../../services/AcademicApplicationService';
import { useMigration } from './MigrationProvider';
import { useAuthStore, useRestrictionsStore } from '../index';
import { patchUseSearchItems } from '../../components/Patches/PatchSearchItems';
import $ from 'jquery';

/**
 * Auth Migration Wrapper
 *
 * This component handles the gradual migration from Context API to Zustand stores.
 * It provides the same interface as the original App component but uses stores when migration is enabled.
 */
export const AuthMigrationWrapper = ({ children }: { children: any }) => {
  const { flags } = useMigration();
  const authStore = useAuthStore();
  const restrictionsStore = useRestrictionsStore();

  const isAuthenticatedFromStore = useAuthStore(state => !!state.user);
  const userFromStore = useAuthStore(state => state.user);
  const groupsFromStore = useAuthStore(state => state.groups);
  const authorizationFromStore = useAuthStore(state => state.authorization);

  // Legacy state (used when migration is disabled)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(true);
  const [authError, setAuthError] = useState('');
  const [authorization, setAuthorization] = useState('');
  const [groups, setGroups] = useState<any[]>([]);
  const [user, setUser] = useState<any>(null);
  const [userApiKey, setUserApiKey] = useState<any[]>([]);
  const [resultados, setResultados] = useState<any[]>([]);
  const [names, setNames] = useState<any[]>([]);
  const [namesBuscador, setNamesBuscador] = useState<any[]>([]);
  const [load, setLoad] = useState<boolean>(false);
  const [lista, setLista] = useState<any[]>([]);

  // user restrictions (legacy)
  const [restrictBrowseAll, setRestrictBrowseAll] = useState(true);
  const [restrictGridDisplay, setRestrictGridDisplay] = useState(true);
  const [restrictGridResultCount, setRestrictGridResultCount] = useState(true);
  const [restrictGraphDisplay, setRestrictGraphDisplay] = useState(true);
  const [restrictSources, setRestrictSources] = useState(false);
  const [restrictChemical, setRestrictChemical] = useState(true);
  const [restrictPathwayDisplay, setRestrictPathwayDisplay] = useState(true);
  const [restrictSort, setRestrictSort] = useState(true);
  const [restrictSortOrder, setRestrictSortOrder] = useState(true);

  // user type state
  const [userType, setUserType] = useState(null);

  // nav state
  const [view, setView] = useState('');
  const [idents, setIdents] = useState('');
  const [source, setSource] = useState('');
  const [tab, setTab] = useState('');
  const [searchItems, setSearchItems] = patchUseSearchItems();
  const [hasValidParams, setHasValidParams] = useState(false);

  // Info sidebar state
  const [selectedInfoItem, setSelectedInfoItem] = useState(null);
  const [geneContent, setGeneContent] = useState({
    isActive: false,
    geneInfo: { symbol: '', id: null, name: '' },
  });

  // Mappings state
  const [mappingContent, setMappingContent] = useState({
    isActive: false,
  });

  $('.popover-header').hide();

  // Auth initialization function
  async function onLoad() {
    if (flags.useAuthStore) {
      // Use the AuthStore's initialization method
      await authStore.initializeAuth();
    } else {
      // Legacy initialization
      setIsAuthenticating(true);
      try {
        const user = await getCurrentUser();
        const session = await refreshSession();
        const userInfo = session.tokens.idToken.payload;
        const userGroups = userInfo['cognito:groups'];

        const userWithAttributes = {
          ...userInfo,
          ...user,
        };

        setUser(userWithAttributes as any);
        setAuthorization(session.tokens.idToken.toString());
        setGroups(userGroups as any);
        setIsAuthenticated(true);
      } catch (e) {
        setIsAuthenticated(false);
        setGroups([]);
        setAuthorization('');
        await signOut();
      } finally {
        setIsAuthenticating(false);
      }
    }
  }

  // Get current user info for components
  const getCurrentUserInfo = () => {
    if (flags.useAuthStore) {
      const isAuthenticated = isAuthenticatedFromStore;
      return isAuthenticated
        ? {
            isLogged: true,
            role: groupsFromStore?.[0] || 'user',
            userType: authStore.userType,
          }
        : null;
    } else {
      return isAuthenticated
        ? {
            isLogged: true,
            role: groups?.[0] || 'user',
            userType: userType,
          }
        : null;
    }
  };

  const currentUser = getCurrentUserInfo();

  // Initialize auth on mount
  useEffect(() => {
    if (flags.useAuthStore) {
      // Use AuthStore initialization
      authStore.initializeAuth();
    } else {
      // Use legacy initialization
      (async () => {
        try {
          await onLoad();
        } catch (error) {
          console.error('Error during onLoad:', error);
        }
      })();
    }
  }, []);

  // Setup consent listener
  useEffect(() => {
    const currentUserData = flags.useAuthStore ? userFromStore : user;
    const currentIsAuthenticated = flags.useAuthStore ? isAuthenticatedFromStore : isAuthenticated;

    if (currentIsAuthenticated && currentUserData?.sub) {
      setupConsentListener((currentUserData as any).sub);
    }
  }, [
    flags.useAuthStore,
    flags.useAuthStore ? isAuthenticatedFromStore : isAuthenticated,
    flags.useAuthStore ? userFromStore?.sub : (user as any)?.sub,
  ]);

  // Fetch user type when authenticated
  useEffect(() => {
    const fetchUserType = async () => {
      const currentUserData = flags.useAuthStore ? userFromStore : user;
      const currentIsAuthenticated = flags.useAuthStore ? isAuthenticatedFromStore : isAuthenticated;

      if (currentIsAuthenticated && currentUserData?.email) {
        try {
          const statusResponse = await AcademicApplicationService.getAcademicStatus(
            (currentUserData as any).email,
          );
          const type = statusResponse.trialType || statusResponse.userType || null;

          if (flags.useAuthStore) {
            authStore.setUserType(type);
          } else {
            setUserType(type);
          }
        } catch (error) {
          console.error('Error fetching user type:', error);
        }
      } else {
        if (flags.useAuthStore) {
          authStore.setUserType(null);
        } else {
          setUserType(null);
        }
      }
    };

    fetchUserType();
  }, [
    flags.useAuthStore,
    flags.useAuthStore ? isAuthenticatedFromStore : isAuthenticated,
    flags.useAuthStore ? userFromStore?.email : (user as any)?.email,
  ]);

  // Update restrictions based on auth state
  useEffect(() => {
    const currentIsAuthenticated = flags.useAuthStore ? isAuthenticatedFromStore : isAuthenticated;
    const currentGroups = flags.useAuthStore ? groupsFromStore : groups;

    if (flags.useRestrictionsStore) {
      // Use Zustand store for restrictions
      restrictionsStore.updateRestrictions(currentIsAuthenticated, currentGroups);
    } else {
      // Use legacy state for restrictions
      setRestrictGridDisplay(!currentIsAuthenticated);

      if (!currentIsAuthenticated || !currentGroups) {
        setRestrictGridResultCount(10 as any);
      } else if (currentGroups?.includes('ROLE_TRIAL')) {
        setRestrictGridResultCount(30 as any);
      } else {
        setRestrictGridResultCount(false as any);
      }

      setRestrictBrowseAll(currentGroups.includes('ROLE_ACADEMIC'));
      setRestrictGraphDisplay(!currentIsAuthenticated || currentGroups.includes('ROLE_ACADEMIC'));
      setRestrictSources(currentGroups.includes('ROLE_ACADEMIC') ? ('CURATED' as any) : (false as any));
      setRestrictPathwayDisplay(!currentIsAuthenticated || currentGroups.includes('ROLE_ACADEMIC'));
      setRestrictChemical(!currentIsAuthenticated || currentGroups.includes('ROLE_ACADEMIC'));
      setRestrictSort(!currentIsAuthenticated || currentGroups.includes('ROLE_ACADEMIC'));
      setRestrictSortOrder(!currentIsAuthenticated || currentGroups.includes('ROLE_ACADEMIC'));
    }
  }, [
    flags.useAuthStore,
    flags.useRestrictionsStore,
    flags.useAuthStore ? isAuthenticatedFromStore : isAuthenticated,
    flags.useAuthStore ? groupsFromStore : groups,
  ]);

  // Prepare context value based on migration flags
  const getContextValue = () => {
    if (flags.useAuthStore && flags.useRestrictionsStore) {
      // Use stores for auth and restrictions
      const isAuthenticated = isAuthenticatedFromStore;
      return {
        // Auth from store (using specific selectors for better reactivity)
        isAuthenticated,
        setIsAuthenticated: value => (value ? null : authStore.logout()), // Only logout supported
        authorization: authorizationFromStore,
        setAuthorization: authStore.setAuthorization,
        groups: groupsFromStore,
        setGroups: authStore.setGroups,
        userApiKey: authStore.userApiKey,
        setUserApiKey: authStore.setUserApiKey,
        authError: authStore.authError,
        setAuthError: authStore.setAuthError,
        user: userFromStore,
        setUser: authStore.setUser,

        // Restrictions from store
        restrictGridDisplay: restrictionsStore.restrictGridDisplay,
        restrictGridResultCount: restrictionsStore.restrictGridResultCount,
        restrictBrowseAll: restrictionsStore.restrictBrowseAll,
        restrictGraphDisplay: restrictionsStore.restrictGraphDisplay,
        restrictSources: restrictionsStore.restrictSources,
        restrictChemical: restrictionsStore.restrictChemical,
        restrictPathwayDisplay: restrictionsStore.restrictPathwayDisplay,

        // Navigation (legacy for now)
        view,
        setView,
        idents,
        setIdents,
        source,
        setSource,
        tab,
        setTab,
        searchItems,
        setSearchItems,
        selectedInfoItem,
        setSelectedInfoItem,
        hasValidParams,
        setHasValidParams,
        geneContent,
        setGeneContent,
        mappingContent,
        setMappingContent,
      };
    } else if (flags.useAuthStore) {
      // Use store for auth, legacy for restrictions
      const isAuthenticated = isAuthenticatedFromStore;
      return {
        // Auth from store (using specific selectors for better reactivity)
        isAuthenticated,
        setIsAuthenticated: value => (value ? null : authStore.logout()),
        authorization: authorizationFromStore,
        setAuthorization: authStore.setAuthorization,
        groups: groupsFromStore,
        setGroups: authStore.setGroups,
        userApiKey: authStore.userApiKey,
        setUserApiKey: authStore.setUserApiKey,
        authError: authStore.authError,
        setAuthError: authStore.setAuthError,
        user: userFromStore,
        setUser: authStore.setUser,

        // Restrictions from legacy state
        restrictGridDisplay,
        restrictGridResultCount,
        restrictBrowseAll,
        restrictGraphDisplay,
        restrictSources,
        restrictChemical,
        restrictPathwayDisplay,

        // Navigation (legacy)
        view,
        setView,
        idents,
        setIdents,
        source,
        setSource,
        tab,
        setTab,
        searchItems,
        setSearchItems,
        selectedInfoItem,
        setSelectedInfoItem,
        hasValidParams,
        setHasValidParams,
        geneContent,
        setGeneContent,
        mappingContent,
        setMappingContent,
      };
    } else {
      // Use legacy state for everything
      return {
        isAuthenticated,
        setIsAuthenticated,
        authorization,
        setAuthorization,
        groups,
        setGroups,
        userApiKey,
        setUserApiKey,
        authError,
        setAuthError,
        user,
        setUser,
        view,
        setView,
        idents,
        setIdents,
        source,
        setSource,
        tab,
        setTab,
        searchItems,
        setSearchItems,
        selectedInfoItem,
        setSelectedInfoItem,
        hasValidParams,
        setHasValidParams,
        geneContent,
        setGeneContent,
        mappingContent,
        setMappingContent,
        restrictGridDisplay,
        restrictGridResultCount,
        restrictBrowseAll,
        restrictGraphDisplay,
        restrictSources,
        restrictChemical,
        restrictPathwayDisplay,
      };
    }
  };

  return children({
    contextValue: getContextValue(),
    currentUser: flags.useAuthStore ? userFromStore : user,
  });
};
